import { Injectable } from '@angular/core';

import { IGradeUnitDayListItemDTO } from '@app/infrastructure/models/grade-unit/grade-unit-day';
import { environment } from '@env/environment';
import { ApiService } from './api.service';
import { UserStateService } from '../state/user-state.service';
import {
  ClassroomAssignmentDayUnitListItemDTO,
  IClassroomAssignmentDayUnitListItemDTO,
  IClassroomAssignmentListItemDTO,
} from '@app/infrastructure/models/classroom-assignment';
import { Observable, of, zip } from 'rxjs';
import { first, map, switchMap, tap } from 'rxjs/operators';
import { GradeUnitDayService } from './grade-unit-day.service';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ClassroomAssignmentService {
  private controllerRoute: string;
  private url: string;

  constructor(
    private apiService: ApiService,
    private gradeUnitDayService: GradeUnitDayService,
    private userStateService: UserStateService,
  ) {
    this.userStateService.getUserSession().subscribe((user) => {
      if (user && user.teacher?.id) {
        this.controllerRoute = `teachers/${user.teacher?.id}/classrooms`;
        this.url = `${environment.apiRoute}/${this.controllerRoute}`;
      } else if (user && user.student?.id) {
        this.controllerRoute = `students/${user.student?.id}`;
        this.url = `${environment.apiRoute}/${this.controllerRoute}`;
      }
    });
  }

  public getPagedClassroomAssignments(
    classroomId: string,
    page: number,
    size: number,
  ): Observable<any[]> {
    const endpoint = `${this.url}/${classroomId}/assignments?page=${page}&pageSize=${size}`;

    return this.apiService.get<any[]>(endpoint);
  }

  public getStudentClassroomAssignmentsByUnit(
    unitNumber: number,
  ): Observable<IClassroomAssignmentDayUnitListItemDTO[]> {
    const endpoint = `${this.url}/units/${unitNumber}/days?page=0&pageSize=100`;

    return this.apiService.get<IClassroomAssignmentDayUnitListItemDTO[]>(
      endpoint,
    );
  }

  public getClassroomAssignments(
    classroomId: string,
    gradingPeriodId: string = '',
  ): Observable<IClassroomAssignmentDayUnitListItemDTO[]> {
    let params = new HttpParams();
    if (gradingPeriodId) {
      params = params.set('gradingPeriodId', gradingPeriodId);
    }
    const endpoint = `${this.url}/${classroomId}/grading-period/${gradingPeriodId}/units`;
    return this.apiService.get<IClassroomAssignmentDayUnitListItemDTO[]>(
      endpoint,
      { params },
    );
  }

  public getClassroomAssignmentById(
    classroomId: string,
    assignmentId: number,
  ): Observable<IClassroomAssignmentDayUnitListItemDTO> {
    return this.getClassroomAssignments(classroomId).pipe(
      map((asn) => asn.find((a) => a.assignmentId === assignmentId)),
      first(),
    );
  }

  public getClassroomAssignmentByDayId(
    classroomId: string,
    dayId: number,
  ): Observable<IClassroomAssignmentDayUnitListItemDTO> {
    return this.getClassroomAssignments(classroomId).pipe(
      map((asn) => asn.find((a) => a.gradeUnitDayId === dayId)),
      first(),
    );
  }

  public getGradeUnitDayAssignments(
    classroomId: string,
    gradeNumber: string,
    unitNumber: number,
  ): Observable<IClassroomAssignmentDayUnitListItemDTO[]> {
    const results = 'results';
    // 1. Zip the two observable arrays together
    // 2. transform days into assignments
    return zip(
      this.getPagedClassroomAssignments(classroomId, 0, 100).pipe(
        map((items) => {
          return items[results];
        }),
      ),
      this.gradeUnitDayService
        .getGradeUnitDayListByUnitNumber(gradeNumber, unitNumber.toString())
        .pipe(map((gud) => gud.results)),
    ).pipe(
      switchMap((arr) => {
        const asns = arr[0];
        const days = arr[1];
        const assignments: IClassroomAssignmentDayUnitListItemDTO[] = [];

        days.forEach((d) => {
          // Check for existing assignment...
          const found: IClassroomAssignmentDayUnitListItemDTO = asns.find(
            (asn: IClassroomAssignmentDayUnitListItemDTO) => {
              return asn.gradeUnitDayId === d.dayId;
            },
          );
          if (found) {
            // Populate missing assignment fields...
            const classroomAssignmentDueDate = 'classroomAssignmentDueDate';
            // From aliased fields
            if (!found.dayCoverImageUrl)
              found.dayCoverImageUrl = d.dayCoverImageUrl;
            if (!found.assignmentDueDate)
              found.assignmentDueDate = found[classroomAssignmentDueDate];

            // From Grade Unit Day ob
            found.dayNumber = d.dayNumber;
            found.dayStartPage = d.dayStartPage;
            found.dayEndPage = d.dayEndPage;
            found.isQuiz = d.isQuiz;

            assignments.push(found);
          } else {
            // Create new assignment...
            const n = Object.assign(
              new ClassroomAssignmentDayUnitListItemDTO(),
              d,
            );
            // map properties missed by Object.assign
            n.gradeUnitDayId = d.dayId;
            n.gradeUnitId = n.unitId;

            assignments.push(n);
          }
        });
        return of(assignments);
      }),
    );
  }

  public assignGradeUnitDayToClassroom(
    payload: IClassroomAssignmentListItemDTO,
  ): Observable<IGradeUnitDayListItemDTO> {
    const endpoint = `${this.url}/${payload.classroomId}/assignments`;

    return this.apiService.post<
      IGradeUnitDayListItemDTO,
      IClassroomAssignmentListItemDTO
    >(endpoint, payload);
  }

  public updateAssignGradeUnitDayToClassroom(
    classAssignment: IClassroomAssignmentListItemDTO,
  ): Observable<IGradeUnitDayListItemDTO> {
    const endpoint = `${this.url}/${classAssignment.classroomId}/assignments/${classAssignment.assignmentId}`;
    const { dueDate } = classAssignment;
    const dateStr = dueDate.toLocaleDateString();

    return this.apiService.patch<IGradeUnitDayListItemDTO, { dueDate: Date }>(
      endpoint,
      { dueDate },
    );
  }

  public deleteAssignment(
    classroomId: string,
    asnId: number,
  ): Observable<IGradeUnitDayListItemDTO> {
    const endpoint = `${this.url}/${classroomId}/assignments/${asnId}`;
    return this.apiService.delete(endpoint);
  }
}
